import { useCallback } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, BaseDropdownContainerUI, ICON_COLOR } from '@zep-us/zus';
import { CheckSmallIcon, MinusSmallIcon, PlusSmallIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';
import { type CategoryNode, NONE_VALUE } from '@/views/build/features/category-tree';
import { type PlainSelectionProps } from '../components/PlainSelection';
import { ColumnUI } from './ColumnUI';
export const AccordionSelection = ({
  value,
  subValue,
  label,
  options,
  error,
  isActive,
  onChange,
  onSubChange,
  onHover,
  onHoverLeave,
  onSubHover,
  onSubHoverLeave
}: PlainSelectionProps & SubSelectionProps) => {
  const handleChange = (nodeId: string) => {
    const node = options?.find(option => option.nodeId === nodeId);
    if (node) {
      onChange(node);
    }
  };
  return <div className={cn('flex flex-1 flex-col h-full min-h-0 gap-4xs', !isActive && 'opacity-[0.32]')} data-sentry-component="AccordionSelection" data-sentry-source-file="AccordionSelection.tsx">
      <ColumnUI.Header label={label} required error={error} data-sentry-element="unknown" data-sentry-source-file="AccordionSelection.tsx" />
      <div className="-m-[4px] min-h-0 flex-1 overflow-auto p-[4px]">
        {isActive && <Accordion type="single" collapsible={false} value={value?.nodeId} onValueChange={handleChange}>
            {options?.map((option, index) => {
          const hasSubMenus = option.subNodes.length > 0;
          const isSelected = value?.nodeId === option.nodeId;
          return <AccordionItem key={option.nodeId} value={option.nodeId} className="group border-none">
                  <AccordionTrigger asChild className={cn('hover:no-underline', !!error && index === 0 && 'focus-ring', '[&[data-state=open]>svg]:rotate-0')}>
                    <ColumnUI.SelectButton isSelected={isSelected} className={'font-[600]'} onMouseEnter={() => onHover?.(option)} onMouseLeave={onHoverLeave} rightSlot={<AccordionTriggerIndicator isSelected={isSelected} isCollapsible={hasSubMenus} />}>
                      {option.label}
                    </ColumnUI.SelectButton>
                  </AccordionTrigger>

                  <AccordionCollapsibleContent isVisible={hasSubMenus} options={option.subNodes} subValue={subValue} onSubChange={onSubChange} onSubHover={onSubHover} onSubHoverLeave={onSubHoverLeave} />
                </AccordionItem>;
        })}
          </Accordion>}
      </div>
    </div>;
};
const AccordionTriggerIndicator = ({
  isSelected,
  isCollapsible
}: {
  isSelected: boolean;
  isCollapsible: boolean;
}) => {
  if (isCollapsible) {
    return <>
        <MinusSmallIcon size="sm" fill={ICON_COLOR.neutral} className="hidden group-data-[state=open]:block" />
        <PlusSmallIcon size="sm" fill={ICON_COLOR.neutral} className="group-data-[state=open]:hidden" />
      </>;
  }
  if (isSelected) {
    return <CheckSmallIcon size="sm" />;
  }
  return <></>;
};
const AccordionCollapsibleContent = ({
  isVisible,
  options,
  subValue,
  onSubChange,
  onSubHover,
  onSubHoverLeave
}: SubSelectionProps & {
  isVisible: boolean;
}) => {
  const isSelected = useCallback((nodeId: string) => {
    if (!subValue) return nodeId === NONE_VALUE;
    return subValue.nodeId === nodeId;
  }, [subValue]);
  if (!isVisible) return null;
  return <AccordionContent asChild className="mb-2xs rounded-sm border border-opacity pb-2xs shadow-xs" data-sentry-element="AccordionContent" data-sentry-component="AccordionCollapsibleContent" data-sentry-source-file="AccordionSelection.tsx">
      <BaseDropdownContainerUI className="border-none px-xs pb-3xs pt-xs shadow-none" data-sentry-element="BaseDropdownContainerUI" data-sentry-source-file="AccordionSelection.tsx">
        {options?.map(option => <ColumnUI.SelectButton size="md" className="text-body-md-semibold" key={option.nodeId} isSelected={isSelected(option.nodeId)} onClick={() => onSubChange(option)} onMouseEnter={() => onSubHover?.(option)} onMouseLeave={onSubHoverLeave}>
            {option.label}
          </ColumnUI.SelectButton>)}
      </BaseDropdownContainerUI>
    </AccordionContent>;
};
type SubSelectionProps = {
  options?: CategoryNode[];
  subValue?: CategoryNode;
  onSubChange: (value: CategoryNode) => void;
  onSubHover?: (value: CategoryNode) => void;
  onSubHoverLeave?: () => void;
};